import { useMutation, useQueryClient } from '@tanstack/react-query';

import type {
  PatchAdminV1StoresStoreIdWorkflowTemplatesIdBody,
  PostAdminV1StoresStoreIdWorkflowTemplatesBody,
  PostAdminV1StoresStoreIdWorkflowTemplatesIdDuplicateBody,
  WorkflowTemplate,
} from '@nucleus-commerce/api-client/admin';

import { workflowTemplatesApi } from '../api/workflows-api';

import { workflowKeys } from './workflow-keys';

type QueryClient = ReturnType<typeof useQueryClient>;

function remember(queryClient: QueryClient, template: WorkflowTemplate) {
  queryClient.setQueryData(workflowKeys.detail(template.storeId, template.id), template);
  return queryClient.invalidateQueries({ queryKey: workflowKeys.list(template.storeId) });
}

/** 409 ALREADY_EXISTS on `name`, 422 on `stages[N]…`; the editor shows them. */
export function useCreateWorkflowTemplate(storeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: PostAdminV1StoresStoreIdWorkflowTemplatesBody) =>
      workflowTemplatesApi.create(storeId, body),
    meta: { successMessage: 'workflows:toasts.created', silentError: true },
    onSuccess: (template) => remember(queryClient, template),
  });
}

export function useUpdateWorkflowTemplate(storeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id: string;
      body: PatchAdminV1StoresStoreIdWorkflowTemplatesIdBody;
    }) => workflowTemplatesApi.update(storeId, id, body),
    meta: { successMessage: 'workflows:toasts.updated', silentError: true },
    onSuccess: (template) => remember(queryClient, template),
  });
}

/** The previous default loses its flag on the backend, so the whole list is refetched. */
export function useMakeDefaultWorkflowTemplate(storeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => workflowTemplatesApi.makeDefault(storeId, id),
    meta: { successMessage: 'workflows:toasts.madeDefault' },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: workflowKeys.all(storeId) }),
  });
}

/** Copying into another store copies its QC checklists along, so both lists there go stale. */
export function useDuplicateWorkflowTemplate(storeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id: string;
      body: PostAdminV1StoresStoreIdWorkflowTemplatesIdDuplicateBody;
    }) => workflowTemplatesApi.duplicate(storeId, id, body),
    meta: { successMessage: 'workflows:toasts.duplicated', silentError: true },
    onSuccess: (template) =>
      Promise.all([
        remember(queryClient, template),
        queryClient.invalidateQueries({ queryKey: workflowKeys.qcAll(template.storeId) }),
      ]),
  });
}

/** 409 RESOURCE_IN_USE for the default while other templates exist; the dialog shows it. */
export function useDeleteWorkflowTemplate(storeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => workflowTemplatesApi.remove(storeId, id),
    meta: { successMessage: 'workflows:toasts.deleted', silentError: true },
    onSuccess: (_void, id) => {
      queryClient.removeQueries({ queryKey: workflowKeys.detail(storeId, id) });
      return queryClient.invalidateQueries({ queryKey: workflowKeys.list(storeId) });
    },
  });
}
